// ShareLevelSelect (DESIGN §3.18.1): Wer einen Eintrag oder eine Quelle sehen darf – Private
// (Standard) · Team · Department · Company · Specific people (öffnet den AclEditor beim Aufrufer).
// Varianten `compact` (Select-Pill 32 in Tabellenzeilen) · `cards` (Radio-Karte je Stufe mit
// Zielgruppe, Q2/EX3) · `chip` (nur Anzeige). Stufen über der Richtlinie sind gesperrt; ein Wechsel
// auf eine engere Stufe heißt Zurückziehen und braucht eine Bestätigung (§4.2).
import { useState, type ReactNode } from 'react';
import {
  BuildingsIcon,
  GlobeSimpleIcon,
  LockSimpleIcon,
  UserListIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import { ConfirmDialog } from './Dialog';
import { RadioGroup } from './Radio';
import { Select } from './Select';
import { cn } from '../basis/kern';

export type ShareLevel = 'private' | 'people' | 'team' | 'department' | 'company';

export const SHARE_LEVELS: ShareLevel[] = ['private', 'team', 'department', 'company', 'people'];

const META: Record<ShareLevel, { label: string; icon: ReactNode; rank: number }> = {
  private: { label: 'Private', icon: <LockSimpleIcon />, rank: 0 },
  people: { label: 'Specific people', icon: <UserListIcon />, rank: 1 },
  team: { label: 'Team', icon: <UsersThreeIcon />, rank: 2 },
  department: { label: 'Department', icon: <BuildingsIcon />, rank: 3 },
  company: { label: 'Company', icon: <GlobeSimpleIcon />, rank: 4 },
};

export const shareLevelLabel = (level: ShareLevel) => META[level].label;

/** Engere Stufe als vorher (Zurückziehen)? „Specific people“ zählt zwischen Private und Team. */
export const isWithdrawal = (from: ShareLevel, to: ShareLevel) => META[to].rank < META[from].rank;

/** Über der Richtlinie: höher als `maxLevel` (Private ist immer erlaubt). */
export const isAboveLimit = (level: ShareLevel, maxLevel: ShareLevel | undefined) =>
  maxLevel !== undefined && META[level].rank > META[maxLevel].rank;

export interface ShareLevelSelectProps {
  value: ShareLevel;
  onValueChange?: (level: ShareLevel) => void;
  variant?: 'compact' | 'cards' | 'chip';
  /** Zielgruppe je Stufe, z. B. `{ team: 'Credit Cards · 7 people' }` (Unterzeile und Name). */
  audiences?: Partial<Record<ShareLevel, string>>;
  /** Höchste Stufe laut Richtlinie des Admins. */
  maxLevel?: ShareLevel;
  /** Angebotene Stufen (Standard alle). */
  levels?: ShareLevel[];
  /** „Specific people“ gewählt: der Aufrufer öffnet den AclEditor. */
  onSpecificPeople?: () => void;
  /** Bestätigung beim Zurückziehen (Standard an). */
  confirmWithdraw?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
}

export function ShareLevelSelect({
  value,
  onValueChange,
  variant = 'compact',
  audiences = {},
  maxLevel,
  levels = SHARE_LEVELS,
  onSpecificPeople,
  confirmWithdraw = true,
  disabled,
  'aria-label': ariaLabel = 'Who can see it',
  className,
}: ShareLevelSelectProps) {
  const [pending, setPending] = useState<ShareLevel | null>(null);
  const limitText = maxLevel ? `Your admin allows up to ${META[maxLevel].label}` : undefined;

  const apply = (level: ShareLevel) => {
    onValueChange?.(level);
    if (level === 'people') onSpecificPeople?.();
  };
  const request = (next: string) => {
    const level = next as ShareLevel;
    if (level === value) return;
    if (confirmWithdraw && isWithdrawal(value, level)) setPending(level);
    else apply(level);
  };

  if (variant === 'chip') {
    return (
      <span
        className={cn(
          'inline-flex h-chip items-center gap-1 rounded-full bg-badge px-2 text-chip text-badge-fg',
          className,
        )}
      >
        <span className="sr-only">{ariaLabel}: </span>
        <span aria-hidden="true" className="inline-flex [&>svg]:size-3">
          {META[value].icon}
        </span>
        {META[value].label}
        {audiences[value] ? <span className="sr-only">, {audiences[value]}</span> : null}
      </span>
    );
  }

  const options = levels.map((level) => {
    const locked = isAboveLimit(level, maxLevel);
    return { level, locked, description: locked ? limitText : audiences[level] };
  });

  const control =
    variant === 'cards' ? (
      <RadioGroup
        aria-label={ariaLabel}
        variant="card"
        value={value}
        onValueChange={request}
        disabled={disabled}
        className={cn('gap-2 [&>button]:min-h-14 [&>button]:p-3', className)}
        options={options.map(({ level, locked, description }) => ({
          value: level,
          disabled: locked,
          label: (
            <span className="inline-flex items-center gap-2">
              <span aria-hidden="true" className="inline-flex text-fg-secondary [&>svg]:size-4">
                {META[level].icon}
              </span>
              {META[level].label}
            </span>
          ),
          description,
          aside: locked ? <LockSimpleIcon size={16} aria-hidden="true" /> : undefined,
        }))}
      />
    ) : (
      <Select
        variant="pill"
        aria-label={ariaLabel}
        value={value}
        onValueChange={request}
        disabled={disabled}
        icon={META[value].icon}
        className={cn('h-8 px-3 text-label', className)}
        options={options.map(({ level, locked, description }) => ({
          value: level,
          label: META[level].label,
          description,
          disabled: locked,
          leading: locked ? <LockSimpleIcon /> : META[level].icon,
        }))}
      />
    );

  return (
    <>
      {control}
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        tone="danger"
        title={`Change to ${pending ? META[pending].label : ''}?`}
        description={`People who can see it now${audiences[value] ? ` (${audiences[value]})` : ''} lose access. Bar removes it from their search results.`}
        confirmLabel="Withdraw"
        onConfirm={() => {
          if (pending) apply(pending);
          setPending(null);
        }}
      />
    </>
  );
}
